import React, { useMemo, useState, useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styles from './ClientsContent.module.scss';
import userRowStyles from './UserRow.module.scss';
import UserRow from './UserRow';
import ClientsWorkoutsList from './ClientsWorkoutsList';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import ConnectWithoutContactRoundedIcon from '@mui/icons-material/ConnectWithoutContactRounded';
import {
  getFullName,
  isHighSchool as _isHighSchool,
  canImpersonate,
} from '../../utils';
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import AddClientDialog from './AddClientDialog';
import EditClientDialog from './EditClientDialog';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import ListContent from '../composites/ListContent';
import { fetchMemberById, fetchMembersPageByGymId } from '../../api';
import { queryKeys } from '../../api/queryKeys';
import { usePaginatedListQuery } from '../../hooks/usePaginatedListQuery';
import { Product, UserType } from '../../constants';
import {
  useSessionAction,
  useSessionData,
  useUserAction,
} from '../../providers';
import { useErrorContext } from '../../providers/errorProvider';
import BaseDialog from '../../components/DialogWrappers/BaseDialog';
import EditProgram from '../Programs/EditProgram';
import { locales } from '../../constants/locale';

import type { Member, WithOptionalId, AssignedProgram } from '../../types';
import InDepthMember from './InDepthMember';
import {
  AddClientDraftContext,
  DashboardScrollContext,
} from '../../screens/Dashboard';
import TabSlider, { TabSliderTab } from '../composites/TabSlider';

const texts = locales.en.components.clients.clientsContent;

enum ContentTabs {
  Summary = 'Summary',
  Workouts = 'Workouts',
}

const getSummaryContentTitles = (isHighSchool: boolean): string[] => {
  return isHighSchool
    ? ['Athlete Name', 'Current Program', 'Trainer', 'Groups', 'Pin', 'Options']
    : [
        'Client Name',
        'Signup Date',
        'Current Program',
        'Trainer',
        'Subscription',
        'Pin',
        'Options',
      ];
};

const getWorkoutsContentTitles = (): string[] => {
  return ['Athlete Name', 'LAST WORKOUT', 'LAST WEEK', 'LAST MONTH', 'Options'];
};

const ClientsContent: React.FC = () => {
  const { gym, loginTrainer, groups, members, trainers, gymProducts } =
    useSessionData();
  const {
    lookupAccountByEmail,
    impersonateAccount,
    isImpersonating,
    ensureExerciseLibraryLoaded,
  } = useSessionAction();
  const { setGlobalError } = useErrorContext();
  const queryClient = useQueryClient();
  const {
    updateMember,
    createMember,
    createProgram,
    assignProgramAndRefreshMembers,
    updateMemberSubscriptions,
  } = useUserAction();
  const [selectedTab, setSelectedTab] = useState<ContentTabs>(
    ContentTabs.Summary,
  );
  const [selectedClient, setSelectedClient] = useState<string>('');
  const {
    isOpen: isAddClientModalOpen,
    setIsOpen: setIsAddClientModalOpen,
    draft: addClientDraft,
    setDraft: setAddClientDraft,
  } = useContext(AddClientDraftContext);
  const [isImpersonateModalOpen, setIsImpersonateModalOpen] = useState(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [editedMember, setEditedMember] = useState<Member | null>(null);
  const [isLoadingEditMember, setIsLoadingEditMember] = useState(false);
  const [editedProgram, setEditedProgram] =
    useState<WithOptionalId<AssignedProgram> | null>(null);
  const [impersonateEmail, setImpersonateEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isLoadingMemberDetails, setIsLoadingMemberDetails] = useState(false);
  const [selectedMemberForDrillDown, setSelectedMemberForDrillDown] =
    useState<Member | null>(null);
  const { scrollToTop } = useContext(DashboardScrollContext);
  useEffect(() => {
    const selectedMemberId = selectedMemberForDrillDown?.id;
    if (!selectedMemberId) {
      return;
    }
    const latestMember = members[selectedMemberId];
    if (!latestMember) {
      return;
    }
    setSelectedMemberForDrillDown((prev) => {
      if (!prev || prev.id !== latestMember.id || prev === latestMember) {
        return prev;
      }
      return latestMember;
    });
  }, [members, selectedMemberForDrillDown?.id]);

  const membersListQuery = usePaginatedListQuery<Member>({
    enabled: Boolean(gym?.id),
    queryKey: (params) => queryKeys.members.gymPage(gym?.id || '', params),
    queryFn: (params) => fetchMembersPageByGymId(gym!.id, params),
  });

  const filteredMembers = useMemo(() => {
    return membersListQuery.items
      .map((member) => {
        const hydratedMember = members[member.id];
        return {
          ...member,
          ...hydratedMember,
          gymIds:
            hydratedMember?.gymIds ?? member.gymIds ?? (gym ? [gym.id] : []),
          trainerIds: hydratedMember?.trainerIds ?? member.trainerIds ?? [],
          groupIds: hydratedMember?.groupIds ?? member.groupIds ?? [],
          subscriptions:
            hydratedMember?.subscriptions ?? member.subscriptions ?? [],
        };
      });
  }, [gym, members, membersListQuery.items]);

  const membersPagination = {
    page: membersListQuery.page,
    pageSize: membersListQuery.pageSize,
    totalElements: membersListQuery.meta.totalElements,
    totalPages: membersListQuery.meta.totalPages,
    onPageChange: membersListQuery.setPage,
    onPageSizeChange: membersListQuery.setPageSize,
    disabled: membersListQuery.isFetching,
  };

  const invalidateMembersPage = () => {
    void queryClient.invalidateQueries({ queryKey: ['members'] });
  };

  const invalidateTrainersPage = () => {
    void queryClient.invalidateQueries({ queryKey: ['trainers'] });
  };

  if (!gym) {
    return (
      <EmptyState
        icon={<AccountCircleRoundedIcon />}
        text="No Clients Found For Gym"
      />
    );
  }

  const isHighSchool = _isHighSchool(gym);
  const userType: UserType = isHighSchool ? UserType.Athlete : UserType.Client;

  const handleAddClient = async (
    newMember: WithOptionalId<Member>,
    trainerId?: string,
  ): Promise<{ success: boolean; emailAlreadyInUse?: boolean; pinAlreadyInUse?: boolean }> => {
    const result = await createMember(newMember, trainerId);
    if (!result.success) {
      return result;
    }
    setIsAddClientModalOpen(false);
    invalidateMembersPage();
    return result;
  };

  const handleEditClient = async (
    oldMemberData: Member,
    newMemberData: Member,
    selectedProduct: Product | '',
  ): Promise<{ success: boolean; emailAlreadyInUse?: boolean; pinAlreadyInUse?: boolean }> => {
    // Need to first update member data without subscriptions since it can harm subscription if didn't update (like email validation)
    const updateResult = await updateMember(oldMemberData, newMemberData);
    if (!updateResult.success) {
      return updateResult;
    }
    invalidateTrainersPage();
    const success = await updateMemberSubscriptions(
      oldMemberData,
      newMemberData,
      selectedProduct,
      gymProducts,
    );
    if (!success) {
      return { success: false };
    }
    setIsEditClientModalOpen(false);
    setEditedMember(null);
    invalidateMembersPage();
    return { success: true };
  };

  const renderAddClientDialog = () => {
    const closeAddClientDialog = () => {
      setIsAddClientModalOpen(false);
      setAddClientDraft(null);
    };

    return (
      <AddClientDialog
        userType={userType}
        gymMembers={Object.values(members)}
        trainers={Object.values(trainers).map(({ trainer }) => ({
          id: trainer.id,
          name: getFullName(trainer),
        }))}
        onSubmit={handleAddClient}
        isOpen={isAddClientModalOpen}
        onClose={closeAddClientDialog}
        draft={addClientDraft}
        onDraftChange={setAddClientDraft}
      />
    );
  };

  const renderEditClientDialog = () => {
    if (!isEditClientModalOpen) return null;
    return (
      <EditClientDialog
        userType={userType}
        trainers={Object.values(trainers).map(({ trainer }) => ({
          id: trainer.id,
          name: getFullName(trainer),
        }))}
        member={editedMember}
        gymMembers={Object.values(members)}
        isLoading={isLoadingEditMember}
        onSubmit={(newMemberData, newSelectedProduct) =>
          handleEditClient(editedMember!, newMemberData, newSelectedProduct)
        }
        isOpen={isEditClientModalOpen}
        onClose={() => {
          setIsEditClientModalOpen(false);
          setEditedMember(null);
        }}
      />
    );
  };

  const renderImpersonateDialog = () => {
    const renderImpersonateDialogContent = () => {
      const renderSubmitImpersonateButton = () => {
        return (
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={isImpersonating}
              onClick={async () => {
                if (!impersonateEmail) {
                  setEmailError('Email is required');
                  return;
                }
                const verificationCodeResponse =
                  await lookupAccountByEmail(impersonateEmail);
                if (!verificationCodeResponse?.trainerId) {
                  setEmailError('Email not found');
                  return;
                }

                const didImpersonate = await impersonateAccount(
                  verificationCodeResponse,
                );
                if (!didImpersonate) {
                  setEmailError('Unable to impersonate this email');
                  return;
                }
                setIsImpersonateModalOpen(false);
              }}
            >
              {isImpersonating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Impersonate'
              )}
            </Button>
          </Box>
        );
      };

      return (
        <div
          style={{
            padding: 20,
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <EmailIcon sx={{ marginRight: 2 }} color="primary" />
            <Typography color="primary">Email Address</Typography>
          </Box>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            color="primary"
            placeholder="your@email.com"
            name="email"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '15px',
              },
            }}
            autoComplete="email"
            autoFocus
            value={impersonateEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setImpersonateEmail(e.target.value);
              setEmailError('');
            }}
            error={Boolean(emailError)}
            helperText={emailError ? emailError : ''}
          />
          {renderSubmitImpersonateButton()}
        </div>
      );
    };

    return (
      <BaseDialog
        isOpen={isImpersonateModalOpen}
        onClose={() => setIsImpersonateModalOpen(false)}
        title="Impersonate"
        children={renderImpersonateDialogContent()}
        actions={<div />}
      />
    );
  };

  const loadMemberDetails = async (member: Member): Promise<Member | null> => {
    const { data, success, error } = await fetchMemberById(member.id);
    if (!success || !data) {
      setGlobalError(error?.message || 'Error loading member details');
      return null;
    }
    return data;
  };

  const handleEditMemberProgram = async (member: Member) => {
    const detailedMember = await loadMemberDetails(member);
    const currentProgram = detailedMember?.program;
    if (!detailedMember || !currentProgram?.id) {
      return;
    }

    setEditedMember(detailedMember);
    if (Array.isArray(currentProgram.programItems)) {
      setEditedProgram({
        ...currentProgram,
        programItems: currentProgram.programItems ?? [],
      });
      return;
    }
    setGlobalError('Assigned program payload is missing workout details');
  };

  const handleOpenEditClient = async (member: Member) => {
    setIsEditClientModalOpen(true);
    setIsLoadingEditMember(true);
    const detailedMember = await loadMemberDetails(member);
    setIsLoadingEditMember(false);
    if (!detailedMember) {
      setIsEditClientModalOpen(false);
      return;
    }
    setEditedMember(detailedMember);
  };

  const handleCreateNewProgram = async (member: Member) => {
    const trainerId = loginTrainer?.id;
    if (!trainerId) return;
    setEditedMember(member);
    setEditedProgram({
      id: undefined,
      name: '',
      trainerId,
      level: 0,
      fitnessGoal: '',
      programItems: [],
      isPrivate: true,
      startDate: new Date().toISOString(),
    });
  };

  const handleMemberSelect = async (member: Member) => {
    setSelectedClient(member.id);

    // Immediately open drill page
    setSelectedMemberForDrillDown(member);
    setIsLoadingMemberDetails(true);

    scrollToTop();

    const [detailedMember] = await Promise.all([
      loadMemberDetails(member),
      ensureExerciseLibraryLoaded(),
    ]);

    if (detailedMember) {
      setSelectedMemberForDrillDown(detailedMember);
    }

    setIsLoadingMemberDetails(false);
  };

  const renderUserSummaryRow = (data: Member) => {
    return (
      <UserRow
        groups={Object.values(groups)}
        userType={userType}
        archiveMember={async (member) => {
          await handleEditClient(member, { ...member, isActive: false }, '');
        }}
        openEditClientModal={() => {
          void handleOpenEditClient(data);
        }}
        selected={selectedClient === data.id}
        onSelect={() => handleMemberSelect(data)}
        key={data.id}
        member={data}
        trainer={Object.values(trainers)
          .map(({ trainer }) => trainer)
          .find(({ id }) => data.trainerIds.includes(id))}
        onEditProgram={() => handleEditMemberProgram(data)}
        onCreateNewProgram={() => handleCreateNewProgram(data)}
      />
    );
  };

  const clearEditingProgram = () => {
    setEditedProgram(null);
    setEditedMember(null);
  };

  const handleSubmitEditedProgram = async (
    newProgramData: WithOptionalId<AssignedProgram>,
  ): Promise<boolean> => {
    try {
      if (!editedMember || !editedProgram) return false;
      const trainerId = loginTrainer?.id;
      if (!trainerId) return false;
      const { program, success } = !newProgramData.id
        ? await createProgram(newProgramData, trainerId)
        : { success: true, program: newProgramData };
      const assignableProgram = program as AssignedProgram;
      if (!success) return false;
      if (assignableProgram && editedMember) {
        if (!assignableProgram.startDate) {
          assignableProgram.startDate = new Date().toISOString();
        }
        await assignProgramAndRefreshMembers(
          [editedMember.id],
          assignableProgram,
        );
        clearEditingProgram();
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleDashboardBack = () => {
    if (editedMember && editedProgram) {
      clearEditingProgram();
    } else if (editedMember) {
      clearEditingProgram();
    } else if (selectedMemberForDrillDown) {
      setSelectedMemberForDrillDown(null);
      setIsLoadingMemberDetails(false);
    }
  };

  const getDrillInContent = () => {
    if (editedMember && editedProgram) {
      return (
        <EditProgram
          program={editedProgram}
          onClose={() => clearEditingProgram()}
          onSubmit={handleSubmitEditedProgram}
          proceedAfterSave={true}
        />
      );
    }

    if (selectedMemberForDrillDown) {
      if (isLoadingMemberDetails) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </div>
        );
      }

      return <InDepthMember member={selectedMemberForDrillDown} />;
    }

    return null;
  };

  const getImpersonateAction = () => {
    return {
      icon: <ConnectWithoutContactRoundedIcon />,
      label: '',
      onClick: () => setIsImpersonateModalOpen(true),
    };
  };

  const getDashboardContentActions = () => {
    const actions = [
      {
        icon: <AccountCircleRoundedIcon />,
        label: `Add ${userType === UserType.Client ? `A Client` : `An Athlete`}`,
        onClick: () => setIsAddClientModalOpen(true),
      },
    ];

    if (canImpersonate(loginTrainer?.id)) {
      actions.push(getImpersonateAction());
    }
    return actions;
  };

  const getSearchPlaceholder = () => {
    if (!filteredMembers.length) return '';
    return `Search for ${userType}s`;
  };

  const renderSummaryContent = () => {
    const renderListName = () => {
      return (
        <span className={styles.sectionTitle}>
          {texts.PER_ATHELETE_METRICS}
        </span>
      );
    };

    return (
      <div className={styles.contentContainer}>
        {renderListName()}
        <ListContent
          items={filteredMembers}
          isLoading={membersListQuery.isLoading}
          titles={getSummaryContentTitles(isHighSchool)}
          listHeaderClassName={
            isHighSchool
              ? userRowStyles.athleteListGrid
              : userRowStyles.clientListGrid
          }
          pagination={membersPagination}
          ItemComponent={({ data }) => {
            return renderUserSummaryRow(data);
          }}
          EmptyStateComponent={() => (
            <EmptyState
              icon={<AccountCircleRoundedIcon />}
              cta={{
                icon: <AddBoxOutlinedIcon color="primary" />,
                label: `Add ${userType === UserType.Client ? `A Client` : `An Athlete`}`,
                onClick: () => setIsAddClientModalOpen(true),
              }}
              text="No Clients Have Been Added"
            />
          )}
        />
      </div>
    );
  };

  const renderWorkoutsContent = () => {
    return (
      <ClientsWorkoutsList
        members={filteredMembers}
        groups={Object.values(groups)}
        trainers={Object.values(trainers)}
        userType={userType}
        onArchiveMember={async (member) => {
          await handleEditClient(member, { ...member, isActive: false }, '');
        }}
        onOpenEditClient={(member) => {
          void handleOpenEditClient(member);
        }}
        onEditProgram={handleEditMemberProgram}
        onCreateNewProgram={handleCreateNewProgram}
        onOpenAddClientDialog={() => setIsAddClientModalOpen(true)}
        titles={getWorkoutsContentTitles()}
        sectionTitle={texts.PER_ATHELETE_METRICS}
        isActive={selectedTab === ContentTabs.Workouts}
        isLoading={membersListQuery.isLoading}
        pagination={membersPagination}
      />
    );
  };

  const tabSliderTabs: TabSliderTab[] = [
    {
      label: ContentTabs.Summary,
      content: renderSummaryContent(),
    },
    {
      label: ContentTabs.Workouts,
      content: renderWorkoutsContent(),
    },
  ];

  const renderContent = () => {
    if (editedMember) return null;
    // if (isLoading) {
    //   return (
    //     <div className={styles.loading}>
    //       <CircularProgress />
    //     </div>
    //   );
    // }
    return (
      <>
        <TabSlider
          tabs={tabSliderTabs}
          initialTab={0}
          rightActions={null}
          activeTab={selectedTab === ContentTabs.Summary ? 0 : 1}
          setActiveTab={(idx) =>
            setSelectedTab(tabSliderTabs[idx].label as ContentTabs)
          }
        />
      </>
    );
  };

  const isDrilledIn =
    Boolean(editedMember && editedProgram) ||
    Boolean(selectedMemberForDrillDown);
  const title = editedProgram
    ? editedProgram.id
      ? `Edit Program ${editedMember?.program?.name}`
      : 'Add Program'
    : selectedMemberForDrillDown
      ? `${getFullName(selectedMemberForDrillDown)}`
      : `${userType} List`;

  return (
    <ContentDrill
      title={title}
      onBack={isDrilledIn ? () => handleDashboardBack() : undefined}
      isParent
      actions={getDashboardContentActions()}
      searchPlaceholder={getSearchPlaceholder()}
      searchQuery={membersListQuery.searchQuery}
      setSearchQuery={membersListQuery.setSearchQuery}
      disableSearch={false}
      isDrilledIn={isDrilledIn}
      drillInContent={getDrillInContent()}
    >
      {renderContent()}
      {renderAddClientDialog()}
      {renderEditClientDialog()}
      {renderImpersonateDialog()}
    </ContentDrill>
  );
};

export default ClientsContent;
